<template>
  <div class="sport-reg-root team-reg-root">
    <MainNavbar />
    <div class="sport-reg-page team-registration-page">
      <router-link :to="{ name: 'MatchesHub' }" class="back-to-hub">
        <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
        Back to Match Hub
      </router-link>

      <header class="sport-reg-header reg-header">
        <p class="sport-reg-kicker reg-kicker">
          <i :class="theme.icon" aria-hidden="true"></i>
          {{ theme.kicker }}
        </p>
        <h1 class="sport-reg-title reg-title">{{ theme.title }}</h1>
        <p class="sport-reg-lead reg-lead">{{ theme.lead }}</p>
      </header>

      <div
        v-if="teamCreateBlocked"
        class="team-create-block-banner"
        role="alert"
      >
        <i class="fa-solid fa-user-group"></i>
        <div class="team-create-block-banner__text">
          <strong>{{ teamCreateBlockTitle }}</strong>
          <p>{{ teamCreateBlockMessage }}</p>
        </div>
        <router-link class="team-create-block-banner__link" :to="{ name: 'TeamDashboard' }">
          Go to team dashboard
        </router-link>
      </div>

      <div class="registration-container">
        <div class="sport-reg-card registration-card upload-card-shell">
          <div class="steps-wrapper">
            <div class="progress-line">
              <div class="progress-fill" :style="{ width: stepperProgressPercent + '%' }"></div>
            </div>
            <div class="sport-reg-steps reg-steps">
              <div
                v-for="(stepItem, index) in steps"
                :key="index"
                class="sport-reg-step reg-step"
                :class="{
                  active: currentStep === index + 1,
                  completed: currentStep > index + 1,
                }"
              >
                <i :class="theme.stepIcons[index]" aria-hidden="true"></i>
                <span class="reg-step-num">Step {{ index + 1 }}</span>
                <div class="reg-step-label">{{ stepItem.label }}</div>
              </div>
            </div>
          </div>

      <!-- Registration Form -->
          <!-- Step 1: Basic Information -->
          <div v-if="currentStep === 1" class="step-content">
            <div class="step-header seller-style">
              <h2 class="title">
                <i class="fa-solid fa-shield-halved"></i>
                Team basic information
              </h2>
              <p class="step-description">Enter your squad name, contact, and identity.</p>
            </div>

            <form @submit.prevent="validateStep1" class="registration-form seller-form">
              <div class="form-grid">
                <div class="form-column">
                  <div class="form-group">
                    <label for="teamName">
                      <i class="fa-solid fa-signature"></i>
                      Team name *
                    </label>
                    <input
                      id="teamName"
                      v-model="form.team.teamName"
                      type="text"
                      placeholder="e.g. Westlands United"
                      :class="{ error: errors.teamName, 'has-value': !!form.team.teamName }"
                    >
                    <p class="input-hint">This appears on your public team profile</p>
                    <span v-if="errors.teamName" class="error-message">{{ errors.teamName }}</span>
                  </div>

                  <div class="form-group">
                    <label for="teamPhone">
                      <i class="fa-solid fa-phone"></i>
                      Team phone *
                    </label>
                    <input
                      id="teamPhone"
                      v-model="form.team.teamPhone"
                      type="tel"
                      placeholder="+254712345678"
                      :class="{ error: errors.teamPhone, 'has-value': !!form.team.teamPhone }"
                    >
                    <p class="input-hint">
                      This number may be shown to opposing teams so you can coordinate match planning (calls or messages).
                    </p>
                    <span v-if="errors.teamPhone" class="error-message">{{ errors.teamPhone }}</span>
                  </div>
                </div>

                <div class="form-column">
                  <div class="form-group">
                    <label for="teamType">
                      <i class="fa-solid fa-tags"></i>
                      Team type *
                    </label>
                    <div class="category-select">
                      <select
                        id="teamType"
                        v-model="form.team.teamType"
                        :class="{ error: errors.teamType, 'has-value': !!form.team.teamType }"
                      >
                        <option value="" disabled>Select team type</option>
                        <option value="preschool">Pre-School</option>
                        <option value="highschool">High School</option>
                        <option value="academy">Academy / Youth</option>
                        <option value="casual">Casual / Recreational</option>
                        <option value="competitive">Competitive</option>
                        <option value="corporate">Corporate</option>
                        <option value="community">Community</option>
                      </select>
                      <i class="fa-solid fa-chevron-down select-arrow"></i>
                    </div>
                    <span v-if="errors.teamType" class="error-message">{{ errors.teamType }}</span>
                  </div>

                  <div class="form-group">
                    <label>
                      <i class="fa-solid fa-image"></i>
                      Team logo
                    </label>
                    <div class="logo-upload">
                      <div
                        class="logo-preview"
                        :class="{ 'has-logo': logoFile || form.team.teamLogo }"
                        @click="triggerLogoUpload"
                      >
                        <div v-if="!logoFile && !form.team.teamLogo" class="logo-placeholder">
                          <i class="fa-solid fa-camera"></i>
                          <span>Upload team logo</span>
                        </div>
                        <img
                          v-else-if="logoPreview"
                          :src="logoPreview"
                          alt="Team Logo"
                          decoding="async"
                        >
                        <img
                          v-else-if="form.team.teamLogo"
                          :src="form.team.teamLogo"
                          alt="Team Logo"
                          decoding="async"
                        >
                      </div>
                      <input
                        ref="logoInput"
                        type="file"
                        accept="image/*"
                        class="logo-input"
                        @change="handleLogoUpload"
                      >
                      <p class="upload-hint input-hint">Square image works best • PNG or JPG • max 2MB</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="full-width-fields">
                <div class="form-group">
                  <label for="teamBio">
                    <i class="fa-solid fa-file-lines"></i>
                    Team bio *
                  </label>
                  <div class="textarea-wrapper">
                    <textarea
                      id="teamBio"
                      v-model="form.team.teamBio"
                      rows="5"
                      maxlength="500"
                      placeholder="Playing style, values, what makes your team unique..."
                      :class="{ error: errors.teamBio, 'has-value': (form.team.teamBio || '').length > 0 }"
                    ></textarea>
                    <div class="textarea-footer">
                      <div class="counter">{{ form.team.teamBio.length }}/500</div>
                      <div class="formatting-hint">
                        <i class="fa-solid fa-circle-info"></i>
                        Shown on challenges and invites
                      </div>
                    </div>
                  </div>
                  <span v-if="errors.teamBio" class="error-message">{{ errors.teamBio }}</span>
                </div>
              </div>

              <p class="sport-reg-notice sport-reg-footnote">
                <i class="fa-solid fa-user-group" aria-hidden="true"></i>
                You do not need to add players during registration. After your team is created, invite players from
                your team dashboard—they can join your squad and you can track their progress over time.
              </p>

              <div class="step-navigation">
                <div class="step-progress">
                  <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: step1FillPercent + '%' }"></div>
                  </div>
                  <span class="progress-text">{{ step1FillPercent }}% complete</span>
                </div>
                <button type="submit" class="next-btn animated">
                  Continue to location &amp; preferences
                  <i class="fa-solid fa-arrow-right"></i>
                </button>
              </div>
            </form>
          </div>

          <!-- Step 2: Location & Preferences -->
          <div v-if="currentStep === 2" class="step-content">
            <div class="step-header seller-style">
              <h2 class="title">
                <i class="fa-solid fa-location-dot"></i>
                Location &amp; preferences
              </h2>
              <p class="step-description">Home ground, county, and when you like to play.</p>
            </div>

            <form @submit.prevent="validateStep2" class="registration-form seller-form">
              <div class="form-grid">
                <div class="form-column">
                  <div class="form-group">
                    <label for="homeGround">
                      <i class="fa-solid fa-house-chimney"></i>
                      Home ground *
                    </label>
                    <input
                      id="homeGround"
                      v-model="form.team.homeGround"
                      type="text"
                      placeholder="e.g. Kasarani Stadium Annex"
                      :class="{ error: errors.homeGround, 'has-value': !!form.team.homeGround }"
                    >
                    <p class="input-hint">Primary pitch or area name</p>
                    <span v-if="errors.homeGround" class="error-message">{{ errors.homeGround }}</span>
                  </div>

                  <div class="form-group">
                    <label for="homeGroundPinLocation">
                      <i class="fa-solid fa-map-pin"></i>
                      Pin / map link
                    </label>
                    <input
                      id="homeGroundPinLocation"
                      v-model="form.team.homeGroundPinLocation"
                      type="text"
                      placeholder="Coordinates, Google Maps link, or landmark"
                      :class="{ error: errors.homeGroundPinLocation, 'has-value': !!form.team.homeGroundPinLocation }"
                    >
                    <span v-if="errors.homeGroundPinLocation" class="error-message">{{ errors.homeGroundPinLocation }}</span>
                  </div>
                </div>

                <div class="form-column">
                  <div class="form-group">
                    <label for="county">
                      <i class="fa-solid fa-map"></i>
                      County *
                    </label>
                    <div class="category-select">
                      <select
                        id="county"
                        v-model="form.team.county"
                        :class="{ error: errors.county, 'has-value': !!form.team.county }"
                        @change="onCountyChange"
                      >
                        <option value="" disabled>Select county</option>
                        <option
                          v-for="county in availableCounties"
                          :key="county"
                          :value="county"
                        >
                          {{ county }}
                        </option>
                      </select>
                      <i class="fa-solid fa-chevron-down select-arrow"></i>
                    </div>
                    <span v-if="errors.county" class="error-message">{{ errors.county }}</span>
                  </div>

                  <div class="form-group">
                    <label for="constituency">
                      <i class="fa-solid fa-location-crosshairs"></i>
                      Constituency *
                    </label>
                    <div class="category-select">
                      <select
                        id="constituency"
                        v-model="form.team.constituency"
                        :disabled="!form.team.county"
                        :class="{ error: errors.constituency, 'has-value': !!form.team.constituency }"
                      >
                        <option value="" disabled>{{ form.team.county ? 'Select constituency' : 'Select county first' }}</option>
                        <option
                          v-for="constituency in availableConstituencies"
                          :key="constituency"
                          :value="constituency"
                        >
                          {{ constituency }}
                        </option>
                      </select>
                      <i class="fa-solid fa-chevron-down select-arrow"></i>
                    </div>
                    <span v-if="errors.constituency" class="error-message">{{ errors.constituency }}</span>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label>Preferred Match Days</label>
                <div class="days-selection">
                  <button
                    v-for="day in daysOfWeek"
                    :key="day.value"
                    type="button"
                    class="day-pill"
                    :class="{ 'selected': form.preferences.preferredDays.includes(day.value) }"
                    @click="toggleDay(day.value)"
                  >
                    {{ day.label }}
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label>Match Frequency</label>
                <select v-model="form.preferences.matchFrequency">
                  <option value="">Select frequency</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>

              <div class="form-group">
                <label>Competition Level</label>
                <select v-model="form.preferences.competitionLevel">
                  <option value="">Select competition level</option>
                  <option value="friendly">Friendly Matches</option>
                  <option value="local_tournaments">Local Tournaments</option>
                  <option value="league">League Competitions</option>
                  <option value="national">National Competitions</option>
                </select>
              </div>

              <div class="form-group">
                <label>Preferred Time of Day</label>
                <select v-model="form.preferences.preferredTime">
                  <option value="">Select preferred time</option>
                  <option value="morning">Morning (8AM - 12PM)</option>
                  <option value="afternoon">Afternoon (12PM - 5PM)</option>
                  <option value="evening">Evening (5PM - 9PM)</option>
                  <option value="any">Any Time</option>
                </select>
              </div>

              <div class="form-group">
                <label>Skill Level *</label>
                <div class="skill-levels">
                  <div
                    v-for="level in skillLevels"
                    :key="level.value"
                    class="skill-option"
                    :class="{ 'selected': form.team.skillLevel === level.value }"
                    @click="form.team.skillLevel = level.value"
                  >
                    <div class="skill-icon">
                      <i :class="level.icon"></i>
                    </div>
                    <div class="skill-info">
                      <h4>{{ level.label }}</h4>
                      <p>{{ level.description }}</p>
                    </div>
                  </div>
                </div>
                <span v-if="errors.skillLevel" class="error-message">{{ errors.skillLevel }}</span>
              </div>

              <div class="form-group">
                <label for="availability">Team Availability *</label>
                <select
                  id="availability"
                  v-model="form.team.availability"
                  :class="{ 'error': errors.availability }"
                >
                  <option value="">Select availability</option>
                  <option value="weekends">Weekends only</option>
                  <option value="weekdays">Weekdays only</option>
                  <option value="both">Both weekends and weekdays</option>
                  <option value="flexible">Flexible</option>
                </select>
                <span v-if="errors.availability" class="error-message">{{ errors.availability }}</span>
              </div>
            </form>

            <div class="nav-buttons">
              <button type="button" class="nav-btn secondary" @click="currentStep--">
                <i class="fa-solid fa-arrow-left"></i>
                Back
              </button>
              <button type="button" class="nav-btn primary animated" @click="validateStep2">
                Continue to review
                <i class="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          </div>

          <!-- Step 3: Review & Submit -->
          <div v-if="currentStep === 3" class="step-content">
            <div class="step-header seller-style">
              <h2 class="title">
                <i class="fa-solid fa-clipboard-check"></i>
                Review &amp; submit
              </h2>
              <p class="step-description">Confirm details—then create your team in one tap.</p>
            </div>

            <div class="review-section">
              <div class="review-card">
                <h3>Team Information</h3>
                <div class="review-grid">
                  <div class="review-item">
                    <label>Team Name:</label>
                    <span>{{ form.team.teamName }}</span>
                  </div>
                  <div class="review-item">
                    <label>Team Phone:</label>
                    <span>{{ form.team.teamPhone }}</span>
                  </div>
                  <div class="review-item">
                    <label>Team Type:</label>
                    <span>{{ getTeamTypeLabel(form.team.teamType) }}</span>
                  </div>
                  <div class="review-item">
                    <label>Team Bio:</label>
                    <span>{{ form.team.teamBio }}</span>
                  </div>
                  <div class="review-item">
                    <label>Home Ground:</label>
                    <span>{{ form.team.homeGround }}</span>
                  </div>
                  <div class="review-item" v-if="form.team.homeGroundPinLocation">
                    <label>Home Ground Pin Location:</label>
                    <span>{{ form.team.homeGroundPinLocation }}</span>
                  </div>
                  <div class="review-item">
                    <label>County:</label>
                    <span>{{ form.team.county }}</span>
                  </div>
                  <div class="review-item">
                    <label>Constituency:</label>
                    <span>{{ form.team.constituency }}</span>
                  </div>
                  <div class="review-item">
                    <label>Skill Level:</label>
                    <span>{{ getSkillLevelLabel(form.team.skillLevel) }}</span>
                  </div>
                  <div class="review-item">
                    <label>Availability:</label>
                    <span>{{ getAvailabilityLabel(form.team.availability) }}</span>
                  </div>
                </div>
              </div>

              <div class="review-card review-card--footnote">
                <p class="sport-reg-notice member-note">
                  <i class="fa-solid fa-crown" aria-hidden="true"></i>
                  You will be registered as team owner and captain.
                </p>
                <p class="sport-reg-notice sport-reg-footnote member-note">
                  <i class="fa-solid fa-envelope" aria-hidden="true"></i>
                  Players are not added during sign-up. After your team is saved, use
                  <strong>Invite players</strong> on your team dashboard to build your roster and track player progress.
                </p>
              </div>

              <div class="review-card">
                <h3>Preferences</h3>
                <div class="review-grid">
                  <div class="review-item">
                    <label>Preferred Days:</label>
                    <span>{{ getPreferredDaysLabel() }}</span>
                  </div>
                  <div class="review-item">
                    <label>Match Frequency:</label>
                    <span>{{ getMatchFrequencyLabel(form.preferences.matchFrequency) }}</span>
                  </div>
                  <div class="review-item">
                    <label>Competition Level:</label>
                    <span>{{ getCompetitionLevelLabel(form.preferences.competitionLevel) }}</span>
                  </div>
                  <div class="review-item">
                    <label>Preferred Time:</label>
                    <span>{{ getPreferredTimeLabel(form.preferences.preferredTime) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="nav-buttons">
              <button type="button" class="nav-btn secondary" @click="currentStep--">
                <i class="fa-solid fa-arrow-left"></i>
                Back
              </button>
              <button
                type="button"
                class="nav-btn primary animated submit-wide"
                :disabled="isSubmitting || teamCreateBlocked"
                @click="submitRegistration"
              >
                <template v-if="!isSubmitting">
                  <i class="fa-solid fa-rocket"></i>
                  Create team
                </template>
                <template v-else>
                  <i class="fa-solid fa-spinner fa-spin"></i>
                  Creating team…
                </template>
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
    <ThirdpartyFooter />
  </div>
</template>

<script>
import "@/routes/match_hub/shared/sportTeamSignUp.css";
import MainNavbar from "@/components/main/headernfooter/header.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import { useAuthStore } from "@/authen/auth";
import { buildSignInLocation } from "@/utils/authRedirect";
import { kenyaCounties } from "./counties.js";
import { SPORT_SIGNUP_THEME } from "@/routes/match_hub/shared/sportSignUpTheme";
import { buildApiUrl } from "@/config/api";

export default {
  name: "FootballTeamRegistration",
  components: {
    MainNavbar,
    ThirdpartyFooter
  },

  data() {
    return {
      theme: SPORT_SIGNUP_THEME.football,
      currentStep: 1,
      isSubmitting: false,

      logoFile: null,
      logoPreview: null,

      teamCreateBlocked: false,
      teamCreateBlockTitle: '',
      teamCreateBlockMessage: '',

      steps: [
        { label: 'Basic Info' },
        { label: 'Preferences' },
        { label: 'Review' }
      ],

      form: {
        team: {
          teamName: '',
          teamPhone: '',
          teamType: '',
          teamBio: '',
          teamLogo: null,
          homeGround: '',
          homeGroundPinLocation: '',
          county: '',
          constituency: '',
          skillLevel: '',
          availability: ''
        },
        preferences: {
          preferredDays: [],
          matchFrequency: '',
          competitionLevel: '',
          preferredTime: ''
        }
      },
      
      availableCounties: Object.keys(kenyaCounties),
      availableConstituencies: [],

      errors: {},

      daysOfWeek: [
        { value: 'monday', label: 'Mon' },
        { value: 'tuesday', label: 'Tue' },
        { value: 'wednesday', label: 'Wed' },
        { value: 'thursday', label: 'Thu' },
        { value: 'friday', label: 'Fri' },
        { value: 'saturday', label: 'Sat' },
        { value: 'sunday', label: 'Sun' }
      ],

      skillLevels: [
        {
          value: 'beginner',
          label: 'Beginner',
          description: 'Just starting out, recreational play',
          icon: 'fa-solid fa-seedling'
        },
        {
          value: 'intermediate',
          label: 'Intermediate',
          description: 'Regular players with some experience',
          icon: 'fa-solid fa-chart-line'
        },
        {
          value: 'advanced',
          label: 'Advanced',
          description: 'Experienced, competitive players',
          icon: 'fa-solid fa-trophy'
        },
        {
          value: 'elite',
          label: 'Elite',
          description: 'Former professionals or high-level competition',
          icon: 'fa-solid fa-crown'
        }
      ]
    };
  },

  computed: {
    stepperProgressPercent() {
      const n = this.steps?.length || 1;
      return (this.currentStep / n) * 100;
    },
    step1FillPercent() {
      let p = 0;
      if ((this.form.team.teamName || '').trim()) p += 25;
      if ((this.form.team.teamPhone || '').trim()) p += 25;
      if (this.form.team.teamType) p += 20;
      if ((this.form.team.teamBio || '').trim()) p += 25;
      if (this.logoFile || this.form.team.teamLogo) p += 5;
      return Math.min(100, p);
    },
  },

  beforeMount() {
    const auth = useAuthStore();
    auth.checkToken();
    if (!auth.isAuthenticated) {
      this.$router.replace(buildSignInLocation(this.$route.fullPath));
    }
  },

  mounted() {
    const auth = useAuthStore();
    if (!auth.isAuthenticated) return;
    this.loadTeamCreateEligibility();
  },

  methods: {
    authHeaderBearer() {
      const token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    },

    /**
     * Matches POST /api/teams/create rules in team.py (owner or active member).
     */
    async loadTeamCreateEligibility() {
      const headers = this.authHeaderBearer();
      if (!headers.Authorization) {
        this.teamCreateBlocked = false;
        return;
      }
      try {
        const [ownerRes, memberRes] = await Promise.all([
          fetch(buildApiUrl('/api/teams/check-team'), { headers }),
          fetch(buildApiUrl('/api/teams/check-team-member'), { headers }),
        ]);
        const ownerJson = await ownerRes.json().catch(() => ({}));
        const memberJson = await memberRes.json().catch(() => ({}));

        if (ownerRes.ok && ownerJson.has_team === true) {
          this.teamCreateBlocked = true;
          this.teamCreateBlockTitle = 'You already have a team';
          this.teamCreateBlockMessage =
            'Each account can only own one team. Use your dashboard to manage it, or contact support if you need to transfer ownership.';
          return;
        }
        if (memberRes.ok && memberJson.is_team_member === true) {
          this.teamCreateBlocked = true;
          this.teamCreateBlockTitle = 'Already on a squad';
          this.teamCreateBlockMessage =
            'Leave or ask to be removed from your current team before you can register a new one.';
          return;
        }
        this.teamCreateBlocked = false;
        this.teamCreateBlockTitle = '';
        this.teamCreateBlockMessage = '';
      } catch (e) {
        console.warn('Team eligibility check failed:', e);
        this.teamCreateBlocked = false;
      }
    },

    /* ---------------- STEP VALIDATION ---------------- */

    validateStep1() {
      this.errors = {};

      if (!this.form.team.teamName.trim()) {
        this.errors.teamName = 'Team name is required';
      }

      if (!this.form.team.teamPhone.trim()) {
        this.errors.teamPhone = 'Team phone number is required';
      } else if (!this.isValidPhone(this.form.team.teamPhone)) {
        this.errors.teamPhone = 'Please enter a valid phone number (e.g., +254712345678)';
      }

      if (!this.form.team.teamType) {
        this.errors.teamType = 'Select team type';
      }

      if (!this.form.team.teamBio.trim()) {
        this.errors.teamBio = 'Team bio is required';
      } else if (this.form.team.teamBio.length < 10) {
        this.errors.teamBio = 'Team bio should be at least 10 characters';
      }

      if (Object.keys(this.errors).length === 0) {
        this.currentStep = 2;
      }
    },

    validateStep2() {
      this.errors = {};

      if (!this.form.team.homeGround.trim()) {
        this.errors.homeGround = 'Home ground is required';
      }
      if (!this.form.team.county) {
        this.errors.county = 'County is required';
      }
      if (!this.form.team.constituency) {
        this.errors.constituency = 'Constituency is required';
      }
      if (!this.form.team.skillLevel) {
        this.errors.skillLevel = 'Select skill level';
      }
      if (!this.form.team.availability) {
        this.errors.availability = 'Select availability';
      }

      if (Object.keys(this.errors).length === 0) {
        this.currentStep = 3;
      }
    },

    onCountyChange() {
      // Reset constituency when county changes
      this.form.team.constituency = '';
      // Update available constituencies based on selected county
      if (this.form.team.county && kenyaCounties[this.form.team.county]) {
        this.availableConstituencies = kenyaCounties[this.form.team.county];
      } else {
        this.availableConstituencies = [];
      }
    },

    isValidPhone(phone) {
      return /^\+?[1-9]\d{1,14}$/.test(phone);
    },

    /* ---------------- LOGO ---------------- */

    triggerLogoUpload() {
      this.$refs.logoInput.click();
    },

    handleLogoUpload(e) {
      const file = e.target.files[0];
      if (!file) return;

      // Validate file size
      if (file.size > 2 * 1024 * 1024) {
        this.emitToast(
          'Logo too large',
          'The server accepts logos under 2MB. Choose a smaller image or compress it.',
          'error'
        );
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        this.emitToast(
          'Invalid logo format',
          'Use JPEG, PNG, GIF, or WebP — same rules as when uploading during team creation.',
          'error'
        );
        return;
      }

      this.logoFile = file;
      const reader = new FileReader();
      reader.onload = e => {
        this.logoPreview = e.target.result;
        this.form.team.teamLogo = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    /* ---------------- DAYS SELECTION ---------------- */
    toggleDay(day) {
      const index = this.form.preferences.preferredDays.indexOf(day);
      if (index > -1) {
        this.form.preferences.preferredDays.splice(index, 1);
      } else {
        this.form.preferences.preferredDays.push(day);
      }
    },

    /* ---------------- SUBMISSION ---------------- */

    submitRegistration() {
      this.actuallySubmitRegistration();
    },

    async actuallySubmitRegistration() {
      if (this.teamCreateBlocked) {
        this.emitToast(
          this.teamCreateBlockTitle || 'Cannot create team',
          this.teamCreateBlockMessage ||
            'You must leave your current squad before creating a new team.',
          'error'
        );
        return;
      }

      this.isSubmitting = true;

      try {
        const formData = new FormData();

        // Add team data (converting camelCase to snake_case for backend)
        formData.append('team_name', this.form.team.teamName);
        formData.append('team_phone', this.form.team.teamPhone);
        formData.append('team_type', this.form.team.teamType);
        formData.append('team_bio', this.form.team.teamBio);
        formData.append('home_ground', this.form.team.homeGround);
        if (this.form.team.homeGroundPinLocation) {
          formData.append('home_ground_pin_location', this.form.team.homeGroundPinLocation);
        }
        formData.append('county', this.form.team.county);
        formData.append('constituency', this.form.team.constituency);
        formData.append('skill_level', this.form.team.skillLevel);
        formData.append('availability', this.form.team.availability);
        
        // Owner is created by the API; invite additional players after registration
        formData.append('members', JSON.stringify([]));
        
        // Prepare preferences for backend (camelCase to snake_case)
        const preferencesForBackend = {
          preferred_days: this.form.preferences.preferredDays,
          match_frequency: this.form.preferences.matchFrequency || '',
          competition_level: this.form.preferences.competitionLevel || '',
          preferred_time: this.form.preferences.preferredTime || ''
        };
        
        // Add preferences as JSON string
        formData.append('preferences', JSON.stringify(preferencesForBackend));
        
        // Add logo if exists
        if (this.logoFile) {
          formData.append('logo', this.logoFile);
        }

        // Get auth token
        const token = localStorage.getItem('access_token') || 
                     localStorage.getItem('token') || 
                     sessionStorage.getItem('access_token') || 
                     sessionStorage.getItem('token');
        
        if (!token) {
          this.emitToast(
            'Sign in required',
            'You need to be logged in to register a team. Sign in and try again.',
            'error'
          );
          return;
        }

        // Make API call (POST /api/teams/create — see application/routes/teams/team.py)
        const response = await fetch(buildApiUrl('/api/teams/create'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // Note: Don't set Content-Type for FormData - browser sets it automatically with boundary
          },
          body: formData
        });

        if (!response.ok) {
          let errorData = {};
          try {
            errorData = await response.json();
          } catch (e) {
            /* non-JSON body */
          }
          const rawDetail = this.formatTeamCreateErrorDetail(errorData, response.status, response.statusText);
          const { title, message } = this.buildTeamCreateErrorToast(
            response.status,
            rawDetail,
            this.form.team.teamName
          );
          this.emitToast(title, message, 'error');
          return;
        }

        const data = await response.json();
        this.handleSuccess(data);
      } catch (error) {
        console.error('Registration failed:', error);
        const message =
          error && error.message
            ? error.message
            : 'Network error. Check your connection and try again.';
        this.emitToast('Registration failed', message, 'error');
      } finally {
        this.isSubmitting = false;
      }
    },

    handleSuccess(data) {
      const name = (data && data.team_name) || this.form.team.teamName;
      window.dispatchEvent(
        new CustomEvent("show-toast", {
          detail: {
            title: "Team created",
            message: name ? `“${name}” was created successfully.` : "Your team was created successfully.",
            type: "success",
          },
        })
      );
      this.$router.push({ name: "TeamDashboard" }).catch(() => {});
    },

    emitToast(title, message, type = 'error') {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { title, message, type },
        })
      );
    },

    /**
     * Normalizes FastAPI / JSON error bodies from POST /api/teams/create.
     */
    formatTeamCreateErrorDetail(errorData, status, statusText) {
      const d = errorData && errorData.detail;
      if (typeof d === 'string') return d;
      if (Array.isArray(d)) {
        return d
          .map((item) => {
            if (typeof item === 'string') return item;
            if (item && typeof item.msg === 'string') return item.msg;
            return null;
          })
          .filter(Boolean)
          .join(' ');
      }
      if (d && typeof d === 'object' && typeof d.msg === 'string') return d.msg;
      if (typeof errorData?.message === 'string') return errorData.message;
      return statusText || `Request failed (${status})`;
    },

    /**
     * Maps HTTP status + server detail to user-facing toast copy (aligned with team.py).
     */
    buildTeamCreateErrorToast(status, rawDetail, teamName) {
      const detail = (rawDetail || '').trim() || 'Something went wrong. Please try again.';
      let message = detail;
      let title = 'Team registration failed';

      if (status === 401) {
        title = 'Sign in required';
        message =
          'Your session may have expired or the token is missing. Sign in again and retry.';
      } else if (status === 404 && /customer not found/i.test(detail)) {
        title = 'Account not found';
        message =
          'No customer profile is linked to this login. Complete your account setup or contact support.';
      } else if (status === 400) {
        if (/already have a team/i.test(detail)) {
          title = 'You already have a team';
          message = detail;
        } else if (/already on a team/i.test(detail)) {
          title = 'Already on a squad';
          message = detail;
        } else if (/name already exists/i.test(detail)) {
          title = 'Team name already taken';
          const base = teamName ? String(teamName).trim() : '';
          message = base
            ? `${detail} Try “${base} FC”, “${base} United”, or “${base} Warriors”.`
            : detail;
        } else if (/under 2mb|2mb/i.test(detail) || /invalid logo format/i.test(detail)) {
          title = 'Logo issue';
          message = detail;
        } else {
          title = 'Cannot create team';
          message = detail;
        }
      } else if (status === 422) {
        title = 'Invalid form data';
        message = detail;
      } else if (status === 429) {
        title = 'Too many attempts';
        message =
          detail ||
          'Team creation is rate-limited (3 per minute). Wait a moment and submit again.';
      } else if (status === 502) {
        title = 'Logo upload failed';
        message = detail;
      } else if (status >= 500) {
        title = 'Server error';
        message =
          detail === 'Internal server error'
            ? 'The server could not complete registration. Try again shortly or contact support if it continues.'
            : detail;
      }

      return { title, message };
    },

    /* ---------------- FORM RESET ---------------- */
    resetForm() {
      this.form = {
        team: {
          teamName: '',
          teamPhone: '',
          teamType: '',
          teamBio: '',
          teamLogo: null,
          homeGround: '',
          homeGroundPinLocation: '',
          county: '',
          constituency: '',
          skillLevel: '',
          availability: ''
        },
        preferences: {
          preferredDays: [],
          matchFrequency: '',
          competitionLevel: '',
          preferredTime: ''
        }
      };
      this.availableConstituencies = [];
      this.logoFile = null;
      this.logoPreview = null;
      this.errors = {};
    },

    /* ---------------- HELPER METHODS (LABEL GETTERS) ---------------- */
    getTeamTypeLabel(type) {
      const types = {
        preschool: 'Pre-School',
        highschool: 'High School',
        academy: 'Academy/Youth',
        casual: 'Casual/Recreational',
        competitive: 'Competitive',
        corporate: 'Corporate',
        community: 'Community'
      };
      return types[type] || type;
    },

    getSkillLevelLabel(level) {
      const levelObj = this.skillLevels.find(l => l.value === level);
      return levelObj ? levelObj.label : level;
    },

    getAvailabilityLabel(availability) {
      const availabilityMap = {
        weekends: 'Weekends only',
        weekdays: 'Weekdays only',
        both: 'Both weekends and weekdays',
        flexible: 'Flexible'
      };
      return availabilityMap[availability] || availability;
    },

    getPreferredDaysLabel() {
      if (this.form.preferences.preferredDays.length === 0) return 'No preference';
      
      const dayLabels = this.daysOfWeek
        .filter(day => this.form.preferences.preferredDays.includes(day.value))
        .map(day => day.label);
      
      return dayLabels.join(', ');
    },

    getMatchFrequencyLabel(frequency) {
      const frequencyMap = {
        weekly: 'Weekly',
        biweekly: 'Bi-weekly',
        monthly: 'Monthly',
        flexible: 'Flexible'
      };
      return frequencyMap[frequency] || 'Not specified';
    },

    getCompetitionLevelLabel(level) {
      const levelMap = {
        friendly: 'Friendly Matches',
        local_tournaments: 'Local Tournaments',
        league: 'League Competitions',
        national: 'National Competitions'
      };
      return levelMap[level] || 'Not specified';
    },

    getPreferredTimeLabel(time) {
      const timeMap = {
        morning: 'Morning (8AM - 12PM)',
        afternoon: 'Afternoon (12PM - 5PM)',
        evening: 'Evening (5PM - 9PM)',
        any: 'Any Time'
      };
      return timeMap[time] || 'Not specified';
    },
  }
};
</script>


<style scoped>
/* Shell, header, steps, fields, buttons: @/routes/match_hub/shared/sportTeamSignUp.css */

.team-create-block-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1rem;
  max-width: 1000px;
  margin: 0 auto 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border: 1px solid var(--border-color-hover);
  background: var(--background-color3);
  color: var(--text-color1);
}

.team-create-block-banner > i {
  color: var(--border-color-hover);
  font-size: 1.5rem;
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.team-create-block-banner__text {
  flex: 1;
  min-width: 200px;
}

.team-create-block-banner__text strong {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 1rem;
}

.team-create-block-banner__text p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: var(--text-color2);
}

.team-create-block-banner__link {
  align-self: center;
  white-space: nowrap;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background: var(--border-color-hover);
  color: var(--text-color1);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 600;
}

.team-create-block-banner__link:hover {
  filter: brightness(1.08);
}

/* —— Stepper (aligned with SellerProductUpload) —— */
.steps-wrapper {
  margin-bottom: 2rem;
}

.progress-line {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1.25rem;
}

.progress-fill {
  height: 100%;
  background: var(--border-color-hover);
  transition: width 0.4s ease;
  border-radius: 10px;
}

/* reg-steps, step headers, form-grid: sportTeamSignUp.css */

.form-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.full-width-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
}

.seller-form label::before {
  display: none !important;
}

.seller-form .form-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 15px;
  color: var(--text-color1);
  margin-bottom: 8px;
}

.seller-form .form-group label i {
  color: var(--border-color-hover);
  width: 20px;
  text-align: center;
}

/* inputs/selects: sportTeamSignUp.css */

.seller-form .form-group > select {
  width: 100%;
  padding: 11px 14px;
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover1);
  border-radius: 12px;
  font-size: 15px;
  color: var(--text-color1);
  transition: all 0.3s ease;
  font-family: inherit;
  box-sizing: border-box;
}

.seller-form .form-group > select:focus {
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(0, 185, 255, 0.1);
  outline: none;
}

.seller-form .input-hint {
  font-size: 13px;
  color: var(--text-color2);
  margin: 4px 0 0 0;
}

.category-select {
  position: relative;
}

.category-select select {
  width: 100%;
  padding: 14px 40px 14px 16px;
  background: var(--background-color2);
  border: 2px solid var(--border-color-hover);
  border-radius: 12px;
  font-size: 15px;
  color: var(--text-color1);
  appearance: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.category-select select:focus,
.category-select select.has-value {
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(0, 185, 255, 0.1);
  outline: none;
}

.category-select select:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.category-select .select-arrow {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color1);
  pointer-events: none;
  font-size: 12px;
}

.textarea-wrapper {
  position: relative;
}

.textarea-wrapper textarea {
  min-height: 120px;
  resize: vertical;
}

.textarea-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.textarea-footer .counter {
  position: static;
  transform: none;
  font-size: 12px;
  color: var(--text-color2);
  background: rgba(255, 255, 255, 0.05);
  padding: 2px 8px;
  border-radius: 10px;
}

.formatting-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-color2);
}

.formatting-hint i {
  color: var(--border-color-hover);
}

.step-navigation {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 8px;
}

.step-progress {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.step-progress .progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.step-progress .progress-text {
  display: block;
  text-align: center;
  font-size: 14px;
  color: var(--text-color2);
}

/* next-btn, nav-btn, nav-buttons: sportTeamSignUp.css */

.nav-buttons {
  gap: 16px;
  margin-top: 32px;
  align-items: stretch;
}

.nav-buttons .submit-wide {
  flex: 1;
  min-width: min(100%, 220px);
}

/* ================= PROGRESS STEPS ================= */
.progress-section {
  background: var(--background-color1);
  padding: 24px;
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px var(--box-shadow);
  animation: slideDown 0.5s ease-out;
  position: relative;
  overflow: hidden;
}

.progress-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--border-color-hover);
  opacity: 0.3;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.progress-steps {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.progress-steps::before {
  content: '';
  position: absolute;
  top: 20px;
  left: 10%;
  right: 10%;
  height: 2px;
  background: var(--background-color3);
  z-index: 0;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex: 1;
  position: relative;
  z-index: 1;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.step:hover {
  transform: translateY(-2px);
}

.step-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 3px solid var(--background-color3);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color1);
  font-weight: 600;
  color: var(--text-color2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 2;
  box-shadow: 0 2px 4px var(--box-shadow);
}

.step-circle::before {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 2px solid transparent;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.step:hover .step-circle::before {
  border-color: var(--border-color-hover);
  opacity: 0.3;
}

.step.active .step-circle {
  border-color: var(--border-color-hover);
  background: var(--border-color-hover);
  color: var(--text-color1);
  transform: scale(1.1);
  box-shadow: 0 4px 16px var(--box-shadow);
  animation: pulse 2s infinite;
}

.step.active .step-circle::before {
  border-color: var(--border-color-hover);
  opacity: 0.5;
}

.step.completed .step-circle {
  border-color: var(--border-color-hover);
  background: var(--border-color-hover);
  color: var(--text-color1);
  transform: scale(1);
  box-shadow: 0 2px 8px var(--box-shadow);
}

.step.completed .step-check {
  animation: checkmark 0.4s ease-out;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 4px 16px var(--box-shadow);
  }
  50% {
    box-shadow: 0 4px 20px var(--box-shadow);
  }
}

@keyframes checkmark {
  0% {
    transform: scale(0) rotate(-45deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(0deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.step-number {
  font-size: 16px;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.step-check {
  font-size: 20px;
  font-weight: bold;
}

.step-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-color2);
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
}

.step.active .step-label {
  color: var(--border-color-hover);
  font-weight: 600;
  transform: scale(1.05);
}

.step.completed .step-label {
  color: var(--border-color-hover);
}

/* ================= REGISTRATION CARD ================= */
.registration-container {
  max-width: 1000px;
  margin: 0 auto;
  animation: slideUp 0.6s ease-out 0.2s both;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.registration-card {
  background: var(--background-color1);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 8px 24px var(--box-shadow);
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.registration-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: var(--border-color-hover);
}

.registration-card:hover {
  box-shadow: 0 12px 32px var(--box-shadow);
  transform: translateY(-2px);
}

.registration-card.upload-card-shell {
  background: var(--background-color2);
  border-radius: 26px;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.registration-card.upload-card-shell::before {
  display: none;
}

.step-content {
  animation: fadeInSlide 0.5s ease-out;
}

@keyframes fadeInSlide {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.step-header {
  text-align: center;
  margin-bottom: 40px;
  position: relative;
  padding-bottom: 20px;
}

.step-header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: var(--border-color-hover);
  border-radius: 2px;
}

.step-header h2 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-color1);
  margin-bottom: 10px;
  background: var(--border-color-hover);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 3s ease infinite;
}

@keyframes gradientShift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.step-header p {
  color: var(--text-color2);
  font-size: 15px;
  font-weight: 400;
}

/* ================= FORM STYLES ================= */
.registration-form {
  max-width: 600px;
  margin: 0 auto;
}

.registration-form.seller-form {
  max-width: none;
  margin: 0;
}

.form-group {
  margin-bottom: 24px;
  animation: fadeInUp 0.5s ease-out;
  animation-fill-mode: both;
}

.form-group:nth-child(1) { animation-delay: 0.1s; }
.form-group:nth-child(2) { animation-delay: 0.2s; }
.form-group:nth-child(3) { animation-delay: 0.3s; }
.form-group:nth-child(4) { animation-delay: 0.4s; }
.form-group:nth-child(5) { animation-delay: 0.5s; }

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-weight: 600;
  color: var(--text-color1);
  font-size: 15px;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

label::before {
  content: '';
  width: 4px;
  height: 16px;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  border-radius: 2px;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.form-group:hover label::before {
  width: 6px;
  height: 20px;
}

input, select, textarea {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid var(--background-color3);
  border-radius: 12px;
  font-size: 15px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--background-color1);
  color: var(--text-color1);
  font-family: inherit;
}

input:hover, select:hover, textarea:hover {
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 4px var(--box-shadow);
}

input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 4px var(--box-shadow);
  transform: translateY(-2px);
  background: var(--background-color1);
}

select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--background-color3);
}

input.error, select.error, textarea.error {
  border-color: var(--border-color-hover);
  animation: shake 0.4s ease;
}

input.error:focus, select.error:focus, textarea.error:focus {
  box-shadow: 0 0 0 4px var(--box-shadow);
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-8px); }
  75% { transform: translateX(8px); }
}

.error-message {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--border-color-hover);
  font-size: 13px;
  margin-top: 6px;
  font-weight: 500;
  animation: slideDown 0.3s ease-out;
}

.error-message::before {
  content: '⚠';
  font-size: 14px;
}

.char-count {
  text-align: right;
  font-size: 12px;
  color: var(--text-color2);
  margin-top: 6px;
  font-weight: 500;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

textarea:focus + .char-count {
  color: var(--border-color-hover);
}

.field-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-color2);
  margin-top: 6px;
  font-style: italic;
}

.field-hint::before {
  content: '💡';
  font-size: 14px;
}

/* ================= LOGO UPLOAD ================= */
.logo-upload {
  text-align: center;
}

.logo-preview {
  width: 180px;
  height: 180px;
  border: 3px dashed var(--background-color3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, var(--background-color1), var(--background-color3));
  overflow: hidden;
  position: relative;
  box-shadow: 0 2px 4px var(--box-shadow);
}

.logo-preview::before {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  opacity: 0;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: -1;
}

.logo-preview:hover {
  border-color: var(--border-color-hover);
  transform: scale(1.08) rotate(5deg);
  box-shadow: 0 4px 12px var(--box-shadow);
}

.logo-preview:hover::before {
  opacity: 0.1;
}

.logo-preview.has-logo {
  border-style: solid;
  border-color: var(--border-color-hover);
  box-shadow: 0 4px 16px var(--box-shadow);
}

.logo-preview.has-logo:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px var(--box-shadow);
}

.logo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.logo-preview:hover img {
  transform: scale(1.1);
}

.logo-placeholder {
  text-align: center;
  color: var(--text-color2);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.logo-placeholder i {
  font-size: 3rem;
  color: var(--border-color-hover);
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: float 3s ease-in-out infinite;
}

.logo-preview:hover .logo-placeholder i {
  transform: scale(1.2) rotate(10deg);
  color: var(--border-color-hover);
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.logo-placeholder span {
  font-weight: 500;
  font-size: 14px;
}

.logo-input {
  display: none;
}

.upload-hint {
  font-size: 12px;
  color: var(--text-color2);
  margin-top: 8px;
  font-weight: 500;
}

/* ================= BUTTONS ================= */
.btn {
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;
  overflow: hidden;
  text-transform: none;
  letter-spacing: 0.3px;
}

.btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: var(--background-color3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.btn:hover::before {
  width: 0;
  height: 0;
}

.btn i {
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-outline {
  background: var(--background-color1);
  border: 2px solid var(--border-color-hover);
  color: var(--border-color-hover);
}

.btn-outline:hover {
  transform: translateY(-3px);
}

.btn-outline:hover i {
  transform: translateX(-3px);
}

.btn-outline:active {
  transform: translateY(-1px);
}

.btn-primary {
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  color: var(--text-color3);
  position: relative;
}

.btn-primary::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  opacity: 0;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-primary:hover {
  transform: translateY(-3px);
}

.btn-primary:hover::after {
  opacity: 0;
}

.btn-primary:hover i {
  transform: scale(1.2) rotate(5deg);
}

.btn-primary:active {
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-primary:disabled:hover {
  transform: none;
  box-shadow: none;
}

.btn-large {
  padding: 18px 36px;
  font-size: 17px;
  border-radius: 14px;
}

.btn-sm {
  padding: 10px 20px;
  font-size: 13px;
}

.btn-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid var(--background-color3);
  background: var(--background-color1);
  color: var(--text-color2);
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-icon:hover {
  transform: scale(1.15) rotate(5deg);
}

.btn-icon:active {
  transform: scale(1.05);
}

.btn-icon.btn-danger {
  color: var(--border-color-hover);
  border-color: var(--border-color-hover);
}

.btn-icon.btn-danger:hover {
  transform: scale(1.15) rotate(-5deg);
}

/* ================= DAYS SELECTION ================= */
.days-selection {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.day-pill {
  padding: 12px 20px;
  border: 2px solid var(--background-color3);
  border-radius: 25px;
  background: var(--background-color1);
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 600;
  position: relative;
  overflow: hidden;
}

.day-pill::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: var(--border-color-hover);
  transform: translate(-50%, -50%);
  transition: width 0.4s, height 0.4s;
  z-index: 0;
}

.day-pill:hover {
  transform: translateY(-3px) scale(1.05);
}

.day-pill:hover::before {
  width: 100px;
  height: 100px;
  opacity: 0.1;
}

.day-pill.selected {
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  border-color: var(--border-color-hover);
  color: var(--text-color3);
  transform: translateY(-3px) scale(1.05);
  animation: selectPill 0.3s ease-out;
}

.day-pill.selected::before {
  width: 0;
  height: 0;
}

@keyframes selectPill {
  0% {
    transform: scale(0.9);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1.05);
  }
}

/* ================= MEMBERS SECTION ================= */
.members-section {
  max-width: 600px;
  margin: 0 auto;
}

.members-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--background-color3);
}

.members-header h3 {
  margin: 0;
  color: var(--text-color1);
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
}

.members-header h3::before {
  content: '';
  width: 4px;
  height: 24px;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  border-radius: 2px;
}

.owner-notice {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, var(--background-color3), var(--background-color3));
  border-radius: 12px;
  margin-bottom: 16px;
  border: 2px solid var(--border-color-hover);
  animation: slideInLeft 0.5s ease-out;
  box-shadow: 0 2px 4px var(--box-shadow);
}

.owner-notice i {
  color: var(--border-color-hover);
  font-size: 20px;
  animation: bounce 2s ease-in-out infinite;
}

.owner-notice span {
  color: var(--text-color2);
  font-size: 14px;
  font-weight: 600;
  flex: 1;
}

.optional-notice {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  border-radius: 12px;
  margin-bottom: 24px;
  border: 2px solid var(--border-color-hover);
  animation: slideInRight 0.5s ease-out;
  box-shadow: 0 2px 4px var(--box-shadow);
}

.optional-notice i {
  color: var(--border-color-hover);
  font-size: 20px;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.optional-notice:hover i {
  transform: rotate(15deg) scale(1.1);
}

.optional-notice span {
  color: var(--border-color-hover);
  font-size: 14px;
  font-weight: 600;
  flex: 1;
}

.members-list {
  margin-bottom: 30px;
}

.member-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px;
  background: linear-gradient(135deg, var(--background-color1), var(--background-color3));
  border-radius: 14px;
  margin-bottom: 12px;
  border: 2px solid var(--background-color3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: slideInUp 0.4s ease-out;
  animation-fill-mode: both;
}

.member-card:nth-child(1) { animation-delay: 0.1s; }
.member-card:nth-child(2) { animation-delay: 0.2s; }
.member-card:nth-child(3) { animation-delay: 0.3s; }
.member-card:nth-child(4) { animation-delay: 0.4s; }

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.member-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  transform: scaleY(0);
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.member-card:hover {
  transform: translateX(8px);
  box-shadow: 0 4px 12px var(--box-shadow);
  border-color: var(--border-color-hover);
}

.member-card:hover::before {
  transform: scaleY(1);
}

.member-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color1);
  font-size: 22px;
  box-shadow: 0 4px 12px var(--box-shadow);
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
}

.member-card:hover .member-avatar {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 6px 16px var(--box-shadow);
}

.member-info {
  flex: 1;
  min-width: 0;
}

.member-name {
  font-weight: 700;
  color: var(--text-color1);
  margin-bottom: 8px;
  font-size: 17px;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.member-card:hover .member-name {
  color: var(--border-color-hover);
}

.member-details {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.member-position, .member-role {
  font-size: 12px;
  padding: 5px 12px;
  border-radius: 12px;
  font-weight: 600;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.member-position {
  background: var(--background-color3);
  color: var(--text-color2);
  border: 1px solid var(--background-color3);
}

.member-role {
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  color: var(--border-color-hover);
  border: 1px solid var(--border-color-hover);
}

.member-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.empty-state {
  text-align: center;
  padding: 50px 30px;
  color: var(--text-color2);
  border: 3px dashed var(--background-color3);
  border-radius: 16px;
  margin-bottom: 24px;
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fadeIn 0.5s ease-out;
}

.empty-state:hover {
  border-color: var(--border-color-hover);
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  transform: scale(1.02);
}

.empty-state i {
  font-size: 64px;
  color: var(--border-color-hover);
  margin-bottom: 20px;
  opacity: 0.6;
  animation: float 3s ease-in-out infinite;
  display: block;
}

.empty-state p {
  margin-bottom: 24px;
  font-size: 17px;
  font-weight: 500;
  color: var(--text-color1);
}

.captain-selection {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 2px solid var(--background-color3);
}

.captain-selection label {
  color: var(--text-color1);
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.captain-selection label::before {
  content: '👑';
  font-size: 18px;
}

.captain-selection select {
  background: var(--background-color1);
  border: 2px solid var(--background-color3);
  padding: 12px 16px;
}

/* ================= SKILL LEVELS ================= */
.skill-levels {
  display: grid;
  gap: 14px;
  margin-top: 12px;
}

.skill-option {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px;
  border: 2px solid var(--background-color3);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--background-color1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 4px var(--box-shadow);
}

.skill-option::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0.1;
}

.skill-option:hover {
  border-color: var(--border-color-hover);
  transform: translateX(8px) scale(1.02);
  box-shadow: 0 4px 12px var(--box-shadow);
}

.skill-option:hover::before {
  width: 100%;
}

.skill-option.selected {
  border-color: var(--border-color-hover);
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  transform: translateX(8px) scale(1.02);
  box-shadow: 0 6px 20px var(--box-shadow);
  animation: selectSkill 0.4s ease-out;
}

.skill-option.selected::before {
  width: 100%;
  opacity: 0.15;
}

@keyframes selectSkill {
  0% {
    transform: translateX(0) scale(1);
  }
  50% {
    transform: translateX(12px) scale(1.05);
  }
  100% {
    transform: translateX(8px) scale(1.02);
  }
}

.skill-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color3);
  font-size: 24px;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.skill-option:hover .skill-icon {
  transform: scale(1.15) rotate(10deg);
}

.skill-option.selected .skill-icon {
  transform: scale(1.1) rotate(5deg);
  animation: iconPulse 2s ease-in-out infinite;
}

@keyframes iconPulse {
  0%, 100% {
    box-shadow: 0 4px 12px var(--box-shadow);
  }
  50% {
    box-shadow: 0 6px 20px var(--box-shadow);
  }
}

.skill-info {
  flex: 1;
  position: relative;
  z-index: 1;
}

.skill-info h4 {
  margin: 0 0 6px 0;
  color: var(--text-color1);
  font-size: 18px;
  font-weight: 700;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.skill-option:hover .skill-info h4,
.skill-option.selected .skill-info h4 {
  color: var(--border-color-hover);
}

.skill-info p {
  margin: 0;
  font-size: 14px;
  color: var(--text-color2);
  line-height: 1.5;
}

/* ================= REVIEW SECTION ================= */
.review-section {
  max-width: 700px;
  margin: 0 auto 40px;
}

.review-card {
  background: linear-gradient(135deg, var(--background-color1), var(--background-color3));
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 24px;
  border: 2px solid var(--background-color3);
  box-shadow: 0 2px 4px var(--box-shadow);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fadeInUp 0.5s ease-out;
  animation-fill-mode: both;
}

.review-card:nth-child(1) { animation-delay: 0.1s; }
.review-card:nth-child(2) { animation-delay: 0.2s; }
.review-card:nth-child(3) { animation-delay: 0.3s; }

.review-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px var(--box-shadow);
  border-color: var(--border-color-hover);
}

.review-card h3 {
  margin-top: 0;
  margin-bottom: 24px;
  color: var(--text-color1);
  font-size: 20px;
  font-weight: 700;
  padding-bottom: 12px;
  border-bottom: 3px solid;
  border-image: linear-gradient(90deg, var(--border-color-hover), var(--border-color-hover)) 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.review-card h3::before {
  content: '';
  width: 4px;
  height: 24px;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  border-radius: 2px;
}

.review-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}

@media (min-width: 600px) {
  .review-grid {
    grid-template-columns: 1.2fr 2fr;
    gap: 20px;
  }
}

.review-item {
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: var(--background-color1);
  border-radius: 10px;
  border-left: 3px solid var(--border-color-hover);
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.review-item:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 4px var(--box-shadow);
}

.review-item label {
  font-weight: 700;
  color: var(--text-color1);
  margin-bottom: 8px;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.review-item label::before {
  content: '•';
  color: var(--border-color-hover);
  font-size: 18px;
}

.review-item span {
  color: var(--text-color2);
  font-size: 15px;
  line-height: 1.6;
  font-weight: 500;
}

.review-members {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.review-member {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: var(--background-color1);
  border-radius: 12px;
  border: 2px solid var(--background-color3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.review-member::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  transform: scaleY(0);
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.review-member:hover {
  transform: translateX(6px);
  box-shadow: 0 2px 4px var(--box-shadow);
  border-color: var(--border-color-hover);
}

.review-member:hover::before {
  transform: scaleY(1);
}

.review-member.owner-member {
  background: linear-gradient(135deg, var(--background-color3), var(--background-color3));
  border-color: var(--border-color-hover);
  box-shadow: 0 4px 12px var(--box-shadow);
}

.review-member.owner-member::before {
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
}

.member-avatar.small {
  width: 48px;
  height: 48px;
  font-size: 18px;
  box-shadow: 0 3px 10px var(--box-shadow);
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.review-member:hover .member-avatar.small {
  transform: scale(1.1) rotate(5deg);
}

.member-avatar.owner-avatar {
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover)) !important;
  box-shadow: 0 3px 10px var(--box-shadow) !important;
}

.member-details {
  flex: 1;
  min-width: 0;
}

.member-details strong {
  display: block;
  color: var(--text-color1);
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
}

.member-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.member-position, .member-role {
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 10px;
  font-weight: 600;
}

.member-position {
  background: var(--background-color3);
  color: var(--text-color2);
  border: 1px solid var(--background-color3);
}

.member-role {
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  color: var(--border-color-hover);
  border: 1px solid var(--border-color-hover);
}

.member-role.owner-role {
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  color: var(--text-color1);
  border-color: var(--border-color-hover);
}

.captain-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  color: var(--text-color1);
  padding: 5px 12px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 8px var(--box-shadow);
  animation: badgeGlow 2s ease-in-out infinite;
}

@keyframes badgeGlow {
  0%, 100% {
    box-shadow: 0 2px 8px var(--box-shadow);
  }
  50% {
    box-shadow: 0 2px 12px var(--box-shadow);
  }
}

.member-note {
  text-align: center;
  padding: 14px;
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  border-radius: 12px;
  margin-top: 20px;
  color: var(--border-color-hover);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 2px solid var(--border-color-hover);
  animation: fadeIn 0.5s ease-out;
}

.member-note i {
  font-size: 18px;
  color: var(--border-color-hover);
  animation: rotate 3s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.owner-member {
  background: var(--background-color3) !important;
  border: 2px solid var(--border-color-hover) !important;
}

.owner-avatar {
  background: var(--border-color-hover) !important;
}

.owner-role {
  background: var(--border-color-hover) !important;
  color: white !important;
}

/* ================= FORM ACTIONS ================= */
.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 2px solid var(--background-color3);
  gap: 16px;
  animation: fadeInUp 0.6s ease-out 0.3s both;
}

.form-actions .btn {
  min-width: 140px;
}

/* ================= MODAL ================= */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-color2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
  backdrop-filter: blur(8px);
  animation: fadeIn 0.3s ease-out;
}

.modal-content {
  background: var(--background-color1);
  border-radius: 20px;
  width: 100%;
  max-width: 550px;
  max-height: 90vh;
  overflow-y: auto;
  animation: modalSlideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 20px 60px var(--box-shadow);
  position: relative;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.modal-content::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 20px;
  padding: 2px;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  -webkit-mask: linear-gradient(var(--background-color1) 0 0) content-box, linear-gradient(var(--background-color1) 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(var(--background-color1) 0 0) content-box, linear-gradient(var(--background-color1) 0 0);
  mask-composite: exclude;
  opacity: 0.3;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px;
  border-bottom: 2px solid var(--background-color3);
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  border-radius: 20px 20px 0 0;
  position: relative;
}

.modal-header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, var(--border-color-hover), var(--border-color-hover));
  border-radius: 2px;
}

.modal-header h3 {
  margin: 0;
  color: var(--text-color1);
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-header h3 i {
  color: var(--border-color-hover);
  font-size: 24px;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-header:hover h3 i {
  transform: scale(1.1) rotate(5deg);
}

.btn-close {
  background: var(--background-color3);
  border: 2px solid var(--background-color3);
  font-size: 18px;
  color: var(--text-color2);
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.btn-close::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--border-color-hover);
  transform: scale(0);
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-close:hover {
  transform: rotate(90deg) scale(1.1);
}

.btn-close:hover::before {
  transform: scale(1);
}

.btn-close i {
  position: relative;
  z-index: 1;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-body {
  padding: 28px;
  animation: fadeIn 0.4s ease-out 0.1s both;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px 28px;
  border-top: 2px solid var(--background-color3);
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  border-radius: 0 0 20px 20px;
}

/* ================= OPTIONAL MODAL ================= */
.optional-modal {
  max-width: 550px;
}

.optional-modal-content {
  text-align: center;
  padding: 20px;
}

.optional-icon {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: var(--border-color-hover);
  font-size: 48px;
  box-shadow: 0 8px 24px var(--box-shadow);
  animation: iconBounce 2s ease-in-out infinite;
  border: 3px solid var(--border-color-hover);
}

@keyframes iconBounce {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-10px) scale(1.05);
  }
}

.optional-modal-content h4 {
  margin: 0 0 16px 0;
  color: var(--text-color1);
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.optional-modal-content p {
  color: var(--text-color2);
  margin-bottom: 24px;
  font-size: 16px;
  line-height: 1.7;
  font-weight: 500;
}

.optional-modal-content ul {
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
  text-align: left;
  background: var(--background-color3);
  border-radius: 12px;
  padding: 20px;
}

.optional-modal-content li {
  padding: 12px 0;
  color: var(--text-color1);
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid var(--background-color3);
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideInLeft 0.4s ease-out;
  animation-fill-mode: both;
}

.optional-modal-content li:nth-child(1) { animation-delay: 0.1s; }
.optional-modal-content li:nth-child(2) { animation-delay: 0.2s; }
.optional-modal-content li:nth-child(3) { animation-delay: 0.3s; }
.optional-modal-content li:nth-child(4) { animation-delay: 0.4s; }

.optional-modal-content li:hover {
  transform: translateX(8px);
  color: var(--border-color-hover);
}

.optional-modal-content li:last-child {
  border-bottom: none;
}

.optional-modal-content li i {
  color: var(--border-color-hover);
  font-size: 16px;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
}

.optional-modal-content li:hover i {
  transform: scale(1.2) rotate(10deg);
}

.optional-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.optional-actions .btn {
  min-width: 160px;
  justify-content: center;
}

/* ================= CONFIRMATION MODAL ================= */
.confirmation-modal .modal-header {
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  border-bottom: 3px solid var(--border-color-hover);
}

.confirmation-icon {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: var(--text-color1);
  font-size: 48px;
  box-shadow: 0 8px 24px var(--box-shadow);
  animation: confirmationPulse 2s ease-in-out infinite;
  border: 4px solid var(--background-color3);
}

@keyframes confirmationPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 8px 24px var(--box-shadow);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 12px 32px var(--box-shadow);
  }
}

.confirmation-details {
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  border-radius: 14px;
  padding: 20px;
  margin: 24px 0;
  border: 2px solid var(--border-color-hover);
  box-shadow: 0 2px 4px var(--box-shadow);
}

.confirmation-item {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
  padding: 14px;
  background: var(--background-color1);
  border-radius: 10px;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 3px solid var(--border-color-hover);
}

.confirmation-item:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 4px var(--box-shadow);
}

.confirmation-item:last-child {
  margin-bottom: 0;
}

.confirmation-item i {
  color: var(--border-color-hover);
  font-size: 20px;
  transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.confirmation-item:hover i {
  transform: scale(1.2) rotate(10deg);
}

.confirmation-note {
  background: linear-gradient(135deg, var(--background-color3), var(--background-color1));
  padding: 14px;
  border-radius: 12px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--border-color-hover);
  font-weight: 600;
  border: 2px solid var(--border-color-hover);
}

.confirmation-note i {
  color: var(--border-color-hover);
  font-size: 18px;
  animation: rotate 3s linear infinite;
}

.confirmation-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 24px;
  flex-wrap: wrap;
}

/* ================= RESPONSIVE ================= */

/* Large Tablets and Small Desktops */
@media (max-width: 1024px) {
  .registration-container {
    max-width: 100%;
    padding: 0 10px;
  }
  
  .registration-card {
    padding: 32px;
  }
  
  .progress-steps {
    gap: 8px;
  }
  
  .step-circle {
    width: 44px;
    height: 44px;
  }
}

/* Tablets */
@media (max-width: 768px) {
  .team-registration-page {
    padding: 16px;
    padding-top: 70px;
    background: var(--background-color2);
  }
  
  .progress-section {
    padding: 20px;
    border-radius: 14px;
  }
  
  .progress-steps {
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .progress-steps::before {
    display: none;
  }
  
  .step {
    flex: 0 0 calc(50% - 6px);
    margin-bottom: 16px;
  }
  
  .step-label {
    font-size: 11px;
  }
  
  .step-circle {
    width: 42px;
    height: 42px;
    font-size: 14px;
  }
  
  .registration-card {
    padding: 24px;
    border-radius: 16px;
  }
  
  .step-header h2 {
    font-size: 22px;
  }
  
  .step-header p {
    font-size: 14px;
  }
  
  .registration-form {
    max-width: 100%;
  }
  
  .form-group {
    margin-bottom: 20px;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 12px;
    margin-top: 32px;
  }
  
  .form-actions .btn {
    width: 100%;
    justify-content: center;
    padding: 14px 24px;
  }
  
  .members-section {
    max-width: 100%;
  }
  
  .members-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    padding-bottom: 16px;
  }
  
  .members-header h3 {
    font-size: 18px;
  }
  
  .logo-preview {
    width: 160px;
    height: 160px;
  }
  
  .skill-levels {
    gap: 12px;
  }
  
  .skill-option {
    padding: 16px;
    gap: 14px;
  }
  
  .skill-icon {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
  
  .review-section {
    max-width: 100%;
  }
  
  .review-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }
  
  .modal-content {
    margin: 0 12px;
    max-width: calc(100% - 24px);
    border-radius: 16px;
  }
  
  .modal-header {
    padding: 20px;
  }
  
  .modal-body {
    padding: 20px;
  }
  
  .modal-footer {
    padding: 20px;
    flex-direction: column;
  }
  
  .modal-footer .btn {
    width: 100%;
  }
  
  .optional-actions,
  .confirmation-actions {
    flex-direction: column;
    gap: 12px;
  }
  
  .optional-actions .btn,
  .confirmation-actions .btn {
    width: 100%;
    min-width: auto;
  }
  
  .days-selection {
    gap: 8px;
  }
  
  .day-pill {
    padding: 10px 16px;
    font-size: 13px;
  }
}

/* Mobile Phones */
@media (max-width: 480px) {
  .team-registration-page {
    padding: 12px;
    padding-top: 60px;
  }
  
  .progress-section {
    padding: 16px;
    margin-bottom: 16px;
  }
  
  .progress-steps {
    gap: 8px;
  }
  
  .step {
    flex: 0 0 calc(50% - 4px);
    gap: 6px;
  }
  
  .step-circle {
    width: 36px;
    height: 36px;
    font-size: 13px;
    border-width: 2px;
  }
  
  .step-label {
    font-size: 10px;
  }
  
  .registration-card {
    padding: 20px;
    border-radius: 14px;
  }
  
  .step-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
  }
  
  .step-header h2 {
    font-size: 20px;
    margin-bottom: 8px;
  }
  
  .step-header p {
    font-size: 13px;
  }
  
  .step-header::after {
    width: 50px;
    height: 2px;
  }
  
  .form-group {
    margin-bottom: 18px;
  }
  
  label {
    font-size: 14px;
    margin-bottom: 8px;
  }
  
  label::before {
    width: 3px;
    height: 14px;
  }
  
  input, select, textarea {
    padding: 12px 14px;
    font-size: 14px;
    border-radius: 10px;
  }
  
  .logo-preview {
    width: 140px;
    height: 140px;
  }
  
  .logo-placeholder i {
    font-size: 2.5rem;
  }
  
  .btn {
    padding: 12px 20px;
    font-size: 14px;
    border-radius: 10px;
  }
  
  .btn-large {
    padding: 16px 24px;
    font-size: 16px;
  }
  
  .btn-icon {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
  
  .days-selection {
    gap: 6px;
  }
  
  .day-pill {
    padding: 10px 14px;
    font-size: 12px;
    border-radius: 20px;
  }
  
  .members-header h3 {
    font-size: 17px;
  }
  
  .owner-notice,
  .optional-notice {
    padding: 12px 14px;
    font-size: 13px;
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
  
  .owner-notice i,
  .optional-notice i {
    font-size: 18px;
  }
  
  .member-card {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 16px;
  }
  
  .member-avatar {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
  
  .member-name {
    font-size: 16px;
  }
  
  .member-details {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .member-actions {
    width: 100%;
    justify-content: center;
    gap: 8px;
  }
  
  .empty-state {
    padding: 40px 20px;
  }
  
  .empty-state i {
    font-size: 56px;
  }
  
  .empty-state p {
    font-size: 15px;
  }
  
  .skill-levels {
    gap: 10px;
  }
  
  .skill-option {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 16px;
  }
  
  .skill-icon {
    width: 56px;
    height: 56px;
    font-size: 22px;
  }
  
  .skill-info h4 {
    font-size: 16px;
  }
  
  .skill-info p {
    font-size: 13px;
  }
  
  .review-card {
    padding: 20px;
    margin-bottom: 16px;
  }
  
  .review-card h3 {
    font-size: 17px;
    margin-bottom: 16px;
  }
  
  .review-item {
    padding: 10px;
  }
  
  .review-item label {
    font-size: 12px;
  }
  
  .review-item span {
    font-size: 14px;
  }
  
  .review-member {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 14px;
  }
  
  .member-avatar.small {
    width: 44px;
    height: 44px;
    font-size: 16px;
  }
  
  .modal-overlay {
    padding: 12px;
  }
  
  .modal-content {
    margin: 0;
    max-width: 100%;
    border-radius: 16px;
    max-height: 95vh;
  }
  
  .modal-header {
    padding: 20px;
  }
  
  .modal-header h3 {
    font-size: 18px;
  }
  
  .modal-body {
    padding: 20px;
  }
  
  .modal-footer {
    padding: 16px 20px;
  }
  
  .confirmation-icon,
  .optional-icon {
    width: 80px;
    height: 80px;
    font-size: 36px;
  }
  
  .confirmation-details {
    padding: 16px;
  }
  
  .confirmation-item {
    padding: 10px;
    gap: 10px;
  }
}

/* Extra Small Mobile */
@media (max-width: 360px) {
  .team-registration-page {
    padding: 10px;
    padding-top: 60px;
  }
  
  .progress-section {
    padding: 12px;
  }
  
  .step-circle {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
  
  .step-label {
    font-size: 9px;
  }
  
  .registration-card {
    padding: 16px;
  }
  
  .step-header h2 {
    font-size: 18px;
  }
  
  input, select, textarea {
    padding: 10px 12px;
    font-size: 13px;
  }
  
  .btn {
    padding: 10px 16px;
    font-size: 13px;
  }
  
  .logo-preview {
    width: 120px;
    height: 120px;
  }
  
  .day-pill {
    padding: 8px 12px;
    font-size: 11px;
  }
  
  .member-card {
    padding: 14px;
  }
  
  .skill-option {
    padding: 14px;
  }
  
  .modal-content {
    border-radius: 12px;
  }
  
  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 16px;
  }
}

/* Landscape Mobile */
@media (max-width: 768px) and (orientation: landscape) {
  .team-registration-page {
    padding-top: 60px;
  }
  
  .progress-section {
    padding: 16px;
  }
  
  .step {
    flex: 0 0 calc(25% - 6px);
  }
  
  .registration-card {
    padding: 24px;
  }
  
  .modal-content {
    max-height: 85vh;
  }
}

/* Print Styles */
@media print {
  .team-registration-page {
    background: white;
    padding: 0;
  }
  
  .progress-section,
  .form-actions,
  .btn,
  .modal-overlay {
    display: none;
  }
  
  .registration-card {
    box-shadow: none;
    border: 1px solid var(--background-color3);
  }
}
</style>